/** @format */

import type { PropsWithChildren } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { setPlaybackTickerState } from "@/context/playbackTickerStore";
import { useSearchParamNavigate } from "@/hooks/useSearchParamNavigate";
import { RunRouteApi as Route } from "@/routes/routeApis";

export type PlaybackMode = "live" | "lap" | "timeline";

export interface PlaybackState {
  mode: PlaybackMode;
  lap: number | null;
  lapStart: number | null;
  lapEnd: number | null;
  lapProgress: number | null;
  timestamp: number | null;
}

type PlaybackStateInput = Partial<Omit<PlaybackState, "mode">> & {
  mode?: PlaybackMode;
};
type PlaybackStateUpdater =
  | PlaybackStateInput
  | ((prev: PlaybackState) => PlaybackStateInput);

interface PlaybackContextValue extends PlaybackState {
  setPlaybackState: (update: PlaybackStateUpdater) => void;
  clearPlayback: () => void;
}

const DEFAULT_PLAYBACK_STATE: PlaybackState = {
  mode: "live",
  lap: null,
  lapStart: null,
  lapEnd: null,
  lapProgress: null,
  timestamp: null,
};

// Persist context across Vite HMR to prevent provider/consumer mismatch
const PlaybackContext =
  (import.meta.hot?.data?.PlaybackContext as ReturnType<
    typeof createContext<PlaybackContextValue | undefined>
  >) ?? createContext<PlaybackContextValue | undefined>(undefined);

if (import.meta.hot?.data) {
  import.meta.hot.data.PlaybackContext = PlaybackContext;
}

// Removed unused parseInteger and parseFloatValue functions

const deriveMode = (
  lap: number | null,
  timestamp: number | null,
  preferred?: PlaybackMode
): PlaybackMode => {
  if (preferred) {
    return preferred;
  }
  if (timestamp !== null) {
    return "timeline";
  }
  if (lap !== null) {
    return "lap";
  }
  return "live";
};

const normalizeState = (
  prev: PlaybackState,
  nextPartial: PlaybackStateInput
): PlaybackState => {
  const merged = {
    ...prev,
    ...nextPartial,
  };
  const lap = merged.lap ?? null;
  const lapStart = merged.lapStart ?? null;
  const lapEnd = merged.lapEnd ?? null;
  const lapProgress = merged.lapProgress ?? null;
  const timestamp = merged.timestamp ?? null;
  const mode = deriveMode(lap, timestamp, nextPartial.mode);

  if (mode === "live") {
    return { ...DEFAULT_PLAYBACK_STATE };
  }

  return {
    mode,
    lap,
    lapStart,
    lapEnd,
    lapProgress,
    timestamp,
  };
};

const parseStateFromParams = (params: {
  playbackLap?: number;
  lapStart?: number;
  lapEnd?: number;
  lapProgress?: number;
  playbackTimestamp?: number;
}): PlaybackState => {
  const lap = params.playbackLap ?? null;
  const lapStart = params.lapStart ?? null;
  const lapEnd = params.lapEnd ?? null;
  const lapProgress = params.lapProgress ?? null;
  const timestamp = params.playbackTimestamp ?? null;

  const mode = deriveMode(lap, timestamp);

  if (mode === "live") {
    return { ...DEFAULT_PLAYBACK_STATE };
  }

  return {
    mode,
    lap,
    lapStart,
    lapEnd,
    lapProgress,
    timestamp,
  };
};

const buildSearchFromState = (
  state: PlaybackState,
  prevSearch: Record<string, unknown>
): Record<string, unknown> => {
  // Start with previous search params (preserves non-playback params like detailVehicleID)
  const next = { ...prevSearch };

  // Remove playback params
  delete next.playbackLap;
  delete next.lapStart;
  delete next.lapEnd;
  delete next.lapProgress;
  delete next.playbackTimestamp;

  if (state.mode === "live") {
    return next;
  }

  // Add playback params
  if (state.lap !== null) {
    next.playbackLap = state.lap;
  }
  if (state.lapStart !== null) {
    next.lapStart = state.lapStart;
  }
  if (state.lapEnd !== null) {
    next.lapEnd = state.lapEnd;
  }
  if (state.lapProgress !== null) {
    next.lapProgress = state.lapProgress;
  }
  if (state.timestamp !== null) {
    next.playbackTimestamp = state.timestamp;
  }

  return next;
};

export const PlaybackProvider = ({ children }: PropsWithChildren) => {
  const navigate = useSearchParamNavigate();
  const search = Route.useSearch({
    select: s => ({
      playbackLap: s.playbackLap,
      lapStart: s.lapStart,
      lapEnd: s.lapEnd,
      lapProgress: s.lapProgress,
      playbackTimestamp: s.playbackTimestamp,
    }),
    structuralSharing: true,
  });

  const playbackState = useMemo(() => parseStateFromParams(search), [search]);
  const latestStateRef = useRef<PlaybackState>(playbackState);

  useEffect(() => {
    latestStateRef.current = playbackState;
  }, [playbackState]);

  const setPlaybackState = useCallback(
    (update: PlaybackStateUpdater) => {
      const baseState = latestStateRef.current;
      const nextPartial = typeof update === "function" ? update(baseState) : update;
      const normalized = normalizeState(baseState, nextPartial);
      latestStateRef.current = normalized;

      void navigate({
        search: prev => buildSearchFromState(normalized, prev),
        replace: true,
      });
    },
    [navigate]
  );

  // Seed the ticker from the URL-derived playback state, so a shared or
  // reloaded /run?playbackTimestamp=... link resumes at the right position.
  //
  // This used to write to a second, duplicate ticker store
  // (src/stores/playbackTickerStore.ts) that only this file read — the rAF
  // loop in usePlayback and its consumer useRunDataSubscription use the store
  // imported above, so URL restore never actually reached them.
  useEffect(() => {
    setPlaybackTickerState(prev => {
      if (
        prev.timestamp === playbackState.timestamp &&
        prev.lapProgress === playbackState.lapProgress
      ) {
        return {};
      }
      return {
        timestamp: playbackState.timestamp,
        lapProgress: playbackState.lapProgress,
      };
    });
  }, [playbackState.timestamp, playbackState.lapProgress]);

  // NOTE: deliberately no ticker -> playbackState subscription.
  //
  // There used to be one, but it listened to the duplicate store that only
  // this file wrote, so it never observed anything except its own echo of the
  // effect above (which it then discarded as unchanged) — inert in practice.
  //
  // Pointing it at the real ticker store would make it live, and that is the
  // hazard: usePlayback drives the ticker from requestAnimationFrame, and
  // setPlaybackState calls navigate(). Subscribing here would issue a router
  // navigation ~60 times a second during playback. Playback position is
  // written to the URL when the user scrubs (via the PlaybackControls calling
  // setPlaybackState directly), which is the intended granularity.

  const clearPlayback = useCallback(() => {
    latestStateRef.current = { ...DEFAULT_PLAYBACK_STATE };
    void navigate({
      search: prev => {
        const {
          playbackLap: _playbackLap,
          lapStart: _lapStart,
          lapEnd: _lapEnd,
          lapProgress: _lapProgress,
          playbackTimestamp: _playbackTimestamp,
          ...rest
        } = prev;
        return rest;
      },
      replace: true,
    });
  }, [navigate]);

  const value = useMemo<PlaybackContextValue>(
    () => ({
      ...playbackState,
      setPlaybackState,
      clearPlayback,
    }),
    [playbackState, setPlaybackState, clearPlayback]
  );

  return <PlaybackContext.Provider value={value}>{children}</PlaybackContext.Provider>;
};

export const usePlaybackState = (): PlaybackContextValue => {
  const context = useContext(PlaybackContext);
  if (context === undefined) {
    throw new Error(
      "usePlaybackState must be used within a PlaybackProvider. " +
        "Ensure your component is wrapped with <PlaybackProvider>."
    );
  }
  return context;
};

/**
 * Hook to access playback state with graceful fallback for non-playback contexts.
 * Use this in API hooks that may be called from both playback and non-playback pages.
 * For direct playback UI components, use usePlaybackState() instead.
 */
export const useOptionalPlaybackState = (): PlaybackContextValue => {
  const context = useContext(PlaybackContext);
  return (
    context ?? {
      ...DEFAULT_PLAYBACK_STATE,
      setPlaybackState: () => {},
      clearPlayback: () => {},
    }
  );
};

export const useIsPlaybackMode = (): boolean => {
  const { mode } = usePlaybackState();
  return mode !== "live";
};

export const DEFAULT_PLAYBACK_SCOPE = DEFAULT_PLAYBACK_STATE;
